import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { CONSENT_CATEGORIES } from "@/data/legal";

/**
 * Barra de consentimento (primeira visita) + painel de preferências
 * (reaberto a qualquer momento pelo link "Preferências de cookies" no
 * rodapé). Nada de analytics/marketing carrega antes de uma decisão —
 * ver hooks/useCookieConsent.js.
 */
export const CookieConsentBanner = () => {
  const {
    consent,
    decided,
    preferencesOpen,
    acceptAll,
    acceptNecessaryOnly,
    savePreferences,
    closePreferences,
    openPreferences,
  } = useCookieConsent();

  const [draftAnalytics, setDraftAnalytics] = useState(false);

  // Sempre que o painel abre, parte do que já está salvo (não de "desligado"),
  // senão reabrir as preferências pra só olhar já desligaria o analytics.
  useEffect(() => {
    if (preferencesOpen) setDraftAnalytics(consent?.analyticsMarketing ?? false);
  }, [preferencesOpen, consent]);

  const showBar = !decided && !preferencesOpen;

  return (
    <>
      {showBar && (
        <div
          className="fixed inset-x-0 bottom-0 z-[60] border-t border-[var(--mn-gold)]/30 bg-white shadow-[0_-8px_30px_-10px_rgba(0,0,0,0.25)]"
          data-testid="cookie-banner"
          role="region"
          aria-label="Consentimento de cookies"
        >
          <div className="mn-container py-5 flex flex-col md:flex-row md:items-center gap-4">
            <p className="text-sm text-[var(--mn-dark)] leading-relaxed flex-1">
              Usamos cookies necessários para o site funcionar e, com sua permissão, ferramentas de
              analíticos/marketing para entender como o site é usado. Saiba mais na nossa{" "}
              <Link to="/politica-de-cookies" className="underline text-[var(--mn-black)] hover:text-[var(--mn-gold)]">
                Política de Cookies
              </Link>.
            </p>
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={openPreferences}
                data-testid="cookie-banner-preferences"
                className="text-xs uppercase tracking-[0.18em] font-semibold text-[var(--mn-dark)] hover:text-[var(--mn-gold)] transition-colors px-3 py-2.5"
              >
                Preferências
              </button>
              <button
                type="button"
                onClick={acceptNecessaryOnly}
                data-testid="cookie-banner-necessary-only"
                className="text-xs uppercase tracking-[0.18em] font-semibold border border-[var(--mn-black)]/20 px-4 py-2.5 rounded-sm hover:border-[var(--mn-black)] transition-colors"
              >
                Somente necessários
              </button>
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-banner-accept-all"
                className="btn-mn-primary !py-2.5 !px-5 !text-[0.72rem]"
              >
                Aceitar todos
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog
        open={preferencesOpen}
        onOpenChange={(open) => {
          if (!open) closePreferences();
        }}
      >
        <DialogContent data-testid="cookie-preferences-dialog" className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif-display font-extrabold text-xl text-[var(--mn-black)]">
              Preferências de cookies
            </DialogTitle>
            <DialogDescription className="text-[var(--mn-dark)]">
              Escolha quais categorias de cookies você permite. Você pode mudar isso a qualquer
              momento pelo link no rodapé do site.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="flex items-start justify-between gap-4 border border-[var(--mn-line)] rounded-[4px] p-4">
              <div>
                <div className="font-semibold text-sm text-[var(--mn-black)]">
                  {CONSENT_CATEGORIES.necessary.label}
                </div>
                <p className="text-xs text-[var(--mn-dark)] mt-1 leading-relaxed">
                  {CONSENT_CATEGORIES.necessary.description}
                </p>
              </div>
              <Switch checked disabled aria-label="Necessários (sempre ativo)" />
            </div>

            <div className="flex items-start justify-between gap-4 border border-[var(--mn-line)] rounded-[4px] p-4">
              <div>
                <div className="font-semibold text-sm text-[var(--mn-black)]">
                  {CONSENT_CATEGORIES.analyticsMarketing.label}
                </div>
                <p className="text-xs text-[var(--mn-dark)] mt-1 leading-relaxed">
                  {CONSENT_CATEGORIES.analyticsMarketing.description}
                </p>
              </div>
              <Switch
                checked={draftAnalytics}
                onCheckedChange={setDraftAnalytics}
                data-testid="cookie-toggle-analytics"
                aria-label={CONSENT_CATEGORIES.analyticsMarketing.label}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => savePreferences({ analyticsMarketing: draftAnalytics })}
              data-testid="cookie-preferences-save"
              className="btn-mn-primary !py-2.5 !px-6 !text-[0.72rem]"
            >
              Salvar preferências
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsentBanner;
